import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { faBox, faBoxes, faChevronRight, faEllipsisVertical, faFileExcel, faLocationDot, faPen, faPlus, faSeedling, faSlidersH, faTractor, faTruck } from '@fortawesome/free-solid-svg-icons';
import { AggregateRowModel, ColumnModel, FilterSearchBeginEventArgs, FilterSettingsModel, GridComponent, PageSettingsModel, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import { forkJoin, Observable } from 'rxjs';
import * as _ from 'underscore';
import { AuthService } from '../../../../../../@core/auth/auth.service';
import { ErrorService } from '../../../../../../@core/utils/error.service';
import { TenantWordsPipe } from '../../../../../core/pipes/tenant-words.pipe';
import { KwFilterDropdownService } from '../../../../../core/widgets/kw-filter-dropdown/kw-filter-dropdown.service';
import { FilterSettings } from '../../../../../core/widgets/kw-filter-dropdown/models/filter-options';
import { FilterResults } from '../../../../../core/widgets/kw-filter-dropdown/models/filter-results';
import { PlatformConfig } from '../../../../../core/models/platform-config';
import { PlatformConfigService } from '../../../../../core/services/platform-config.service';
import { InventoryLotDTO, SummaryHum } from '../../../../../modules/inventory/models/inventory-lot';
import { InventoryLotAttributeDTO } from '../../../../../modules/inventory/models/lot-attribute';
import { InventoryService } from '../../../../../modules/pick-plan/services/inventory.service';
import { LotActionsService } from '../../../services/lot-actions.service';
import { KW_QUANTITY_FORMAT } from '../../../../../../@core/utils/ejs-constants';

const MOBILE_PAGE_SIZE = 25;

@Component({
  selector: 'lots-list',
  templateUrl: './lots-list.component.html',
  styleUrls: ['./lots-list.component.scss']
})
export class LotsListComponent implements OnInit {
  @ViewChild('actionTemplate', { static: true })
  public actionTemplate: any;

  @ViewChild('grid', { static: false }) grid: GridComponent;

  public faBox = faBox;
  public faBoxes = faBoxes;
  public faChevronRight = faChevronRight;
  public faEllipsisVertical = faEllipsisVertical;
  public faFileExcel = faFileExcel;
  public faLocationDot = faLocationDot;
  public faPen = faPen;
  public faPlus = faPlus;
  public faSeedling = faSeedling;
  public faSlidersH = faSlidersH;
  public faTractor = faTractor;
  public faTruck = faTruck;

  public isLoading = true;
  private initialLoad = true;
  public isAdmin = false;
  public lots: InventoryLotDTO[] = [];
  public allLots: InventoryLotDTO[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;
  public filterSettings: FilterSettingsModel = {
    mode: "Immediate",
    type: "Excel"
  }
  public filter: Object = { type: "CheckBox" };
  public attrabutes?: InventoryLotAttributeDTO[] = [];
  public pageSettings: PageSettingsModel = {
    pageSize: 50,
    pageSizes: [50, 100, 200]
  }
  public aggregates: AggregateRowModel[] = [];

  public aggregate: AggregateRowModel = {
    columns: [
      {
        type: 'Sum',
        field: 'lotNumber',
        format: 'N0',
        footerTemplate: 'Totals:',
      },
      {
        type: 'Sum',
        field: 'totalQtyOnHand',
        format: KW_QUANTITY_FORMAT,
        footerTemplate: '${Sum}'
      },
      {
        type: 'Sum',
        field: 'weight',
        format: 'N0',
        footerTemplate: '${Sum}'
      }
    ]
  };
  private destroyRef = inject(DestroyRef);

  public columns: ColumnModel[] = [];

  /** Lower-cased header names to omit from the grid, tenant-configurable via the "Inventory Lots List Hidden Columns" PlatformConfig (comma-separated). */
  private hiddenColumnNames: string[] = [];

  baseColumns: ColumnModel[] = [];

  public dropdownSettings: FilterSettings = {
    varietySettings: {
      display: true,
      singleSelection: false
    },
    roomSettings: {
      display: true,
      singleSelection: false
    },
    warehouseSettings: {
      display: true,
      singleSelection: false
    },

    locationSettings: {
      display: true,
      singleSelection: false
    },

    lotNumberSettings: {
      display: true,
      singleSelection: false
    },
    inventoryItemSettings: {
      display: true,
      singleSelection: false
    },

  };

  constructor(
    private errorService: ErrorService,
    private lotService: InventoryService,
    public router: Router,
    public tenantWordPipe: TenantWordsPipe,
    public filterService: KwFilterDropdownService,
    private lotActions: LotActionsService,
    public authService: AuthService,
    private platformConfigService: PlatformConfigService
  ) {

  }

  ngOnInit() {
    this.isAdmin = this.authService.hasRole('inventory:admin');

    this.baseColumns = [
      { field: 'lotNumber', headerText: 'Lot Number', autoFit: true, clipMode: "EllipsisWithTooltip" },
      { field: 'productCust', headerText: this.tenantWordPipe.transform("Products"), autoFit: true, clipMode: "EllipsisWithTooltip" },
      { field: 'varietyName', headerText: 'Variety', autoFit: true, clipMode: "EllipsisWithTooltip" },
      { field: 'blockName', headerText: 'Block', autoFit: true, clipMode: "EllipsisWithTooltip" },
      { field: 'farmName', headerText: 'Farm', autoFit: true, clipMode: "EllipsisWithTooltip" },
      { field: 'totalQtyOnHand', headerText: this.tenantWordPipe.transform("Available Qty"), autoFit: true, format: KW_QUANTITY_FORMAT },
      { field: 'locationCust', headerText: this.tenantWordPipe.transform("Location"), autoFit: true, clipMode: "EllipsisWithTooltip" },
      { field: 'weight', headerText: this.tenantWordPipe.transform("Weight"), autoFit: true, format: "N0" },
      { field: 'createdDateObj', headerText: 'Created', autoFit: true, type: 'date', format: { type: 'date', format: 'MM/dd/yy' } },
    ]

    this.updateTable();
  }

  updateTable() {
    this.isLoading = true;
    forkJoin([
      this.lotService.getLots(),
      this.lotService.getInventoryLotAttributes(),
      this.platformConfigService.getAllConfigValues()
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({next:x => {

      x[0].forEach(y => {
        y.locationCust = this.getLocation(y);
        y.productCust = this.getProduct(y);
        y.createdDateObj = y.createdDate ? new Date(y.createdDate) : null;
      });

      this.lots = x[0];
      this.allLots = x[0];
      this.attrabutes = x[1];
      this.hiddenColumnNames = this.parseHiddenColumnNames(x[2]);
      this.mobileVisibleCount = MOBILE_PAGE_SIZE;

      this.setColumns();
      this.isLoading = false;
    }, error:err => {
      this.errorService.handleError(err);
      this.isLoading = false;
    }}
    );
  }

  private parseHiddenColumnNames(configs: PlatformConfig[]): string[] {
    let config = configs?.find(c => c.name === 'Inventory Lots List Hidden Columns' && c.isActive !== false);

    if (!config || !config.value)
      return [];

    return config.value.split(',').map(v => v.trim().toLowerCase()).filter(v => v.length > 0);
  }

  actionBegin(args: FilterSearchBeginEventArgs) {
    if ((!!this.lots && this.lots.length > 0) && (args.requestType === "filterchoicerequest" || args.requestType === "filtersearchbegin")) {
      //Default count is 1k so change it to total length of lots
      args.filterChoiceCount = this.lots.length;
    }
  }


  setColumns() {
    this.columns = [];

    this.columns.push(...this.baseColumns);

    let details = _.uniq(this.lots.flatMap(x => x.attributes), x => x.customAttributeId);

    let hums = this.lots.filter(f => f.summaryHums != null).flatMap(x => x.summaryHums);
    let huds = hums.flatMap(j => j.summaryHuds);


    for (let x of details) {
      for (let est of this.lots) {
        let existingDetail = est.attributes.find(z => z.customAttributeId == x.customAttributeId);

        est[x.customAttributeName.toString()] = existingDetail ? existingDetail.value : '';

      }


      this.columns.push({
        field: x.customAttributeName.toString(),
        headerText: x.customAttributeName.toString(),
        clipMode: "EllipsisWithTooltip",
        autoFit: true,
        filter: this.filter
      });
    }



    let uniqComponents = _.uniq(huds.map(x => x.packageComponentName)).filter(j => j != '');
    let uniqUnitOfMeasures = _.uniq(huds.map(x => x.packageUnitOfMeasure)).filter(j => j != '');


    this.columns.push(...uniqUnitOfMeasures.map(x => <ColumnModel>{ field: x, headerText: x, autoFit: true, format: 'N2' }));
    this.columns.push(...uniqComponents.map(x => <ColumnModel>{ field: x, headerText: x, autoFit: true, format: 'N2' }));

    if (this.isAdmin)
      this.columns.push({ headerText: '', template: this.actionTemplate, width: 70, allowSorting: false, allowFiltering: false, textAlign: 'Center' });

    this.lots.forEach(x => {
      uniqUnitOfMeasures.forEach(f => { x[f] = 0 });
      uniqComponents.forEach(f => { x[f] = 0 });
    });

    if (this.hiddenColumnNames.length > 0) {
      this.columns = this.columns.filter(c => !this.hiddenColumnNames.includes((c.headerText || '').toLowerCase()));
    }

    this.aggregates = [];
    this.aggregates.push(this.aggregate);
  }

  onDataBound() {
    if (this.grid) {
      this.grid.autoFitColumns();  // Use the grid instance to call autoFitColumns
    }
  }

  setFilterResults(results: FilterResults) {
    if (this.initialLoad) {
      this.initialLoad = false;
      results = this.filterService.getFilterState();
    }

    let filteredResults = [...this.allLots.filter(x =>
      (results.selectedFarms.map(y => y.id).includes(x.farmId) || results.selectedFarms.length === 0) &&
      (results.selectedBlocks.map(y => y.id).includes(x.blockId) || results.selectedBlocks.length === 0) &&
      (results.selectedLotNumbers.map(y => y.lotNumber).includes(x.lotNumber) || results.selectedLotNumbers.length === 0) &&
      (results.selectedVarieties.map(y => y.id).includes(x.varietyId) || results.selectedVarieties.length === 0) &&
      (results.selectedFieldmen.map(y => y.itemName).includes(x.fieldman) || results.selectedFieldmen.length === 0) &&
      //(results.selectedCropYears.map(y => y.id).includes(x.cropYearId) || results.selectedCropYears.length === 0) &&
      (this.containsWarehouse(results.selectedWarehouses.map(y => y.id), x.summaryHums) || results.selectedWarehouses.length === 0) &&
      (this.containsRoom(results.selectedRooms.map(y => y.id), x.summaryHums) || results.selectedRooms.length === 0) &&
      (this.containsLocation(results.selectedLocations.map(y => y.id), x.summaryHums) || results.selectedLocations.length === 0) &&
      (this.containsProduct(results.selectedInventoryItems.map(y => y.name), x.summaryHums) || results.selectedInventoryItems.length === 0)
    )];

    this.lots = [...filteredResults];
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }

  public containsWarehouse(ids: number[], hums: SummaryHum[]): boolean {
    return ids.some(id => (hums ?? []).some(h => h.warehouseId === id));
  }

  public containsRoom(ids: number[], hums: SummaryHum[]): boolean {
    return ids.some(id => (hums ?? []).some(h => h.roomId === id));
  }

  public containsLocation(ids: number[], hums: SummaryHum[]): boolean {
    return ids.some(id => (hums ?? []).some(h => h.locationId === id));
  }

  public containsProduct(names: string[], hums: SummaryHum[]): boolean {
    const itemNames = (hums ?? []).flatMap(h => h.summaryHuds ?? []).map(hud => hud.itemName);
    return names.some(name => itemNames.includes(name));
  }

  onRowSelected(args: RowSelectEventArgs) {
    const lot = args?.data as InventoryLotDTO;
    if (lot) this.openDashboard(lot);
  }

  openDashboard(lot: InventoryLotDTO) {
    if (!lot?.id) return;
    this.router.navigateByUrl('/harvest-engine/inventory/lot-dashboard/' + lot.id);
  }

  createLot() {
    this.runAction(this.lotActions.editLot({}));
  }

  editLot(lot: InventoryLotDTO) {
    this.runAction(this.lotActions.editLot(lot));
  }

  editProduct(lot: InventoryLotDTO) {
    this.runAction(this.lotActions.editProduct(lot));
  }

  moveInventory(lot: InventoryLotDTO) {
    this.runAction(this.lotActions.moveInventory(lot));
  }

  adjustQuantity(lot: InventoryLotDTO) {
    this.runAction(this.lotActions.adjustQuantity(lot));
  }

  private runAction(action: Observable<boolean>) {
    action.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(changed => {
      this.grid?.clearSelection();
      if (changed)
        this.updateTable();
    });
  }

  exportExcel() {
    this.grid?.excelExport();
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByLot(_: number, lot: InventoryLotDTO): number | undefined {
    return lot.id;
  }

  public getLocation(data: InventoryLotDTO): string {
    let results = [];

    if (data.summaryHums) {
      for (let summaryHum of data.summaryHums) {
        results.push(`${(summaryHum.warehouse ? summaryHum.warehouse : '')} ${(summaryHum.room ? summaryHum.room : '')} ${(summaryHum.location ? summaryHum.location : '')}`.trim());
      }
    }

    return _.uniq(results).filter(r => r).join(', ');
  }

  public getProduct(data: InventoryLotDTO): string {
    const itemNames = (data.summaryHums ?? [])
      .flatMap(hum => hum.summaryHuds ?? [])
      .filter(hud => hud.lotId == data.id)
      .map(hud => hud.itemName)
      .filter(name => !!name);

    return _.uniq(itemNames).join(', ');
  }


}
